import { ChevronDown } from "lucide-react";
import React, { createContext, useContext, useId, useMemo, useCallback } from "react";

// --- Types ---

type ActiveKeyType = string | string[] | null | undefined;

interface AccordionContextValue {
  activeKey: ActiveKeyType;
  onSelect: (eventKey: string) => void;
  alwaysOpen: boolean;
}

interface ItemContextValue {
  eventKey: string;
  isExpanded: boolean;
  bodyId: string;
  headerId: string;
}

// --- Contexts ---

const AccordionContext = createContext<AccordionContextValue | null>(null);
const ItemContext = createContext<ItemContextValue | null>(null);

function useAccordionContext(): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error("Accordion sub-components must be used within an <Accordion>");
  }
  return ctx;
}

function useItemContext(): ItemContextValue {
  const ctx = useContext(ItemContext);
  if (!ctx) {
    throw new Error("Accordion.Header / Accordion.Body must be used within an <Accordion.Item>");
  }
  return ctx;
}

// --- Helpers ---

function isKeyActive(activeKey: ActiveKeyType, eventKey: string): boolean {
  if (activeKey == null) return false;
  if (Array.isArray(activeKey)) return activeKey.includes(eventKey);
  return activeKey === eventKey;
}

function normaliseToArray(key: ActiveKeyType): string[] {
  if (Array.isArray(key)) return key;
  if (key != null) return [key];
  return [];
}

function toggleKeyInArray(keys: string[], eventKey: string): string[] {
  return keys.includes(eventKey) ? keys.filter((k) => k !== eventKey) : [...keys, eventKey];
}

// --- Accordion (root) ---

interface AccordionProps {
  children: React.ReactNode;
  activeKey?: ActiveKeyType;
  defaultActiveKey?: ActiveKeyType;
  onSelect?: (eventKey: ActiveKeyType) => void;
  alwaysOpen?: boolean;
  className?: string;
}

function AccordionRoot(props: Readonly<AccordionProps>) {
  const { children, activeKey: controlledKey, defaultActiveKey, onSelect, alwaysOpen = false, className } = props;

  const [internalKey, setInternalKey] = React.useState<ActiveKeyType>(defaultActiveKey ?? null);

  // Distinguish between "prop not provided" and "prop provided as undefined"
  const isControlled = "activeKey" in props;
  const activeKey = isControlled ? controlledKey : internalKey;

  const handleSelect = useCallback(
    (eventKey: string) => {
      if (isControlled && onSelect) {
        if (alwaysOpen) {
          onSelect(toggleKeyInArray(normaliseToArray(activeKey), eventKey));
        } else {
          onSelect(activeKey === eventKey ? null : eventKey);
        }
      } else if (!isControlled) {
        if (alwaysOpen) {
          setInternalKey((prev) => toggleKeyInArray(normaliseToArray(prev), eventKey));
        } else {
          setInternalKey((prev) => (prev === eventKey ? null : eventKey));
        }
      }
    },
    [isControlled, onSelect, alwaysOpen, activeKey],
  );

  const contextValue = useMemo(
    () => ({ activeKey, onSelect: handleSelect, alwaysOpen }),
    [activeKey, handleSelect, alwaysOpen],
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      {/*
        Classes are prefixed with `gc-` (rather than reusing Bootstrap's own
        `accordion`/`accordion-item`/etc. names) because Guest Checkout is
        embedded as a web component directly into venue pages without a
        Shadow DOM. Reusing Bootstrap's reserved class names let the host
        page's own accordion CSS collide with ours (state toggled correctly,
        but display was governed by the host's rules), so this component must
        use names no host stylesheet would ever target.
      */}
      <div className={`gc-accordion ${className ?? ""}`.trim()}>{children}</div>
    </AccordionContext.Provider>
  );
}

// --- Accordion.Item ---

interface AccordionItemProps {
  children: React.ReactNode;
  eventKey: string;
  className?: string;
}

function AccordionItem({ children, eventKey, className }: Readonly<AccordionItemProps>) {
  const { activeKey } = useAccordionContext();
  const uniqueId = useId();
  const isExpanded = isKeyActive(activeKey, eventKey);
  const bodyId = `accordion-body-${uniqueId}`;
  const headerId = `accordion-header-${uniqueId}`;

  const itemCtx = useMemo(() => ({ eventKey, isExpanded, bodyId, headerId }), [eventKey, isExpanded, bodyId, headerId]);

  return (
    <ItemContext.Provider value={itemCtx}>
      <div className={`gc-accordion-item ${className ?? ""}`.trim()}>{children}</div>
    </ItemContext.Provider>
  );
}

// --- Accordion.Header ---

interface AccordionHeaderProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

function AccordionHeader({ children, onClick, className }: Readonly<AccordionHeaderProps>) {
  const { onSelect } = useAccordionContext();
  const { eventKey, isExpanded, bodyId, headerId } = useItemContext();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      onSelect(eventKey);
    },
    [onClick, onSelect, eventKey],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();

      // Ensure any consumer-provided click handler still runs for keyboard users.
      onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
      onSelect(eventKey);
    },
    [onClick, onSelect, eventKey],
  );

  return (
    <h2 className={`gc-accordion-header ${className ?? ""}`.trim()}>
      <button
        type="button"
        id={headerId}
        className={`gc-accordion-header__button gcw:flex gcw:w-full gcw:items-center gcw:justify-between gcw:border-0 gcw:bg-transparent gcw:text-left gcw:cursor-pointer gcw:p-4${isExpanded ? " gc-accordion-header--expanded" : " gc-accordion-header--collapsed"}`}
        aria-expanded={isExpanded}
        aria-controls={bodyId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <span className="gcw:flex-1">{children}</span>
        <span
          className={`gc-accordion-header__chevron gcw:ml-2 gcw:inline-block gcw:transition-transform gcw:duration-300${isExpanded ? " gcw:rotate-0" : " gcw:-rotate-180"}`}
          aria-hidden="true"
        >
          <ChevronDown size={24} />
        </span>
      </button>
    </h2>
  );
}

// --- Accordion.Body ---

interface AccordionBodyProps {
  children: React.ReactNode;
  className?: string;
}

function AccordionBody({ children, className }: Readonly<AccordionBodyProps>) {
  const { isExpanded, bodyId, headerId } = useItemContext();

  return (
    <section
      id={bodyId}
      aria-labelledby={headerId}
      className={`gc-accordion-collapse gcw:grid gcw:transition-[grid-template-rows] gcw:duration-300 gcw:ease-in-out${isExpanded ? " gcw:grid-rows-[1fr]" : " gcw:grid-rows-[0fr]"}`}
      {...(!isExpanded ? { inert: true as unknown as boolean, "aria-hidden": true } : {})}
    >
      <div className="gcw:overflow-hidden">
        <div className={`gc-accordion-body ${className ?? ""}`.trim()}>{children}</div>
      </div>
    </section>
  );
}

// --- Compound export ---

const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Header: AccordionHeader,
  Body: AccordionBody,
});

export { Accordion };
export default Accordion;
